import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import useBinanceTickers from '@/hooks/useBinanceTickers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import NewsRail from '@/components/NewsRail';
import { Share2 } from 'lucide-react';

function ShareCoinButton({ coinId, symbol }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState('');

  const open_ = async () => {
    if (!user) { toast.info('Sign in to share coins with friends'); return; }
    try {
      const r = await api.post('/coin_referrals', { coin_id: coinId });
      setLink(window.location.origin + r.data.share_url);
      setOpen(true);
    } catch (_e) { toast.error('Failed'); }
  };

  const copy = () => { navigator.clipboard.writeText(link); toast.success('Link copied'); };
  const socials = [
    { name: 'WhatsApp', color: '#25D366', build: (msg, url) => `https://wa.me/?text=${encodeURIComponent(msg + ' ' + url)}` },
    { name: 'Twitter', color: '#1DA1F2', build: (msg, url) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(msg)}&url=${encodeURIComponent(url)}` },
    { name: 'Telegram', color: '#0088cc', build: (msg, url) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(msg)}` },
  ];
  const msg = `Hey — check out ${symbol} on BITGONODE. If you sign up with my link and trade ${symbol}, we both get +25 USDT free.`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={open_}
        className="border-[#2B3139] bg-[#181A20] text-white hover:bg-[#2B3139] rounded-sm" data-testid="share-coin-btn">
        <Share2 className="w-3.5 h-3.5 mr-1.5" /> Refer this coin
      </Button>
      <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm">
        <DialogHeader><DialogTitle>Share {symbol}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-[#B7BDC6]">Both you and your friend get <span className="text-[#FCD535] font-mono">+25 USDT</span> when they sign up with this link and place their first {symbol} trade.</div>
          <div className="flex gap-2">
            <Input readOnly value={link} className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono text-xs flex-1" />
            <Button onClick={copy} variant="outline" className="border-[#2B3139] bg-[#0B0E11] text-white hover:bg-[#2B3139] rounded-sm">Copy</Button>
          </div>
          <div className="flex gap-2">
            {socials.map(s => (
              <a key={s.name} href={s.build(msg, link)} target="_blank" rel="noreferrer" data-testid={`coin-share-${s.name.toLowerCase()}`}
                className="flex-1 text-center py-2 border rounded-sm text-xs font-medium"
                style={{ background: `${s.color}15`, borderColor: `${s.color}55`, color: s.color }}>
                {s.name}
              </a>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function useLivePrice(basePrice, wsLive) {
  // Prefer WebSocket live price; fall back to simulated fluctuation
  const [price, setPrice] = useState(basePrice);
  const [dir, setDir] = useState('none');
  const last = useRef(basePrice);
  useEffect(() => { setPrice(basePrice); last.current = basePrice; }, [basePrice]);
  useEffect(() => {
    if (wsLive?.price) {
      const p = wsLive.price;
      setDir(p > last.current ? 'up' : p < last.current ? 'down' : 'flat');
      last.current = p;
      setPrice(p);
      return;
    }
    if (!basePrice) return;
    const iv = setInterval(() => {
      const delta = (Math.random() - 0.5) * 0.002;
      const next = Math.max(0, last.current * (1 + delta));
      setDir(next > last.current ? 'up' : 'down');
      last.current = next;
      setPrice(next);
    }, 1200);
    return () => clearInterval(iv);
  }, [basePrice, wsLive?.price]);
  return { price, dir };
}

function OrderBook({ price }) {
  // Generate simulated order book around price
  const rows = useMemo(() => {
    if (!price) return { bids: [], asks: [] };
    const asks = Array.from({ length: 10 }).map((_, i) => {
      const p = price * (1 + 0.0005 * (i + 1));
      const q = Math.random() * 3 + 0.1;
      return { price: p, qty: q, total: p * q };
    });
    const bids = Array.from({ length: 10 }).map((_, i) => {
      const p = price * (1 - 0.0005 * (i + 1));
      const q = Math.random() * 3 + 0.1;
      return { price: p, qty: q, total: p * q };
    });
    return { asks: asks.reverse(), bids };
  }, [price ? Math.round(price * 100) : 0]);
  const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return (
    <div className="text-xs" data-testid="order-book">
      <div className="grid grid-cols-3 px-3 py-2 text-[#848E9C] uppercase tracking-wider text-[10px]">
        <div>Price (USDT)</div><div className="text-right">Amount</div><div className="text-right">Total</div>
      </div>
      <div>
        {rows.asks.map((r, i) => (
          <div key={i} className="grid grid-cols-3 px-3 py-1 hover:bg-[#2B3139] font-mono">
            <div className="text-[#F6465D]">{fmt(r.price)}</div>
            <div className="text-right text-[#EAECEF]">{r.qty.toFixed(4)}</div>
            <div className="text-right text-[#848E9C]">{fmt(r.total)}</div>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 border-y border-[#2B3139] font-mono text-lg text-white">
        {fmt(price || 0)} <span className="text-[#848E9C] text-xs">USDT</span>
      </div>
      <div>
        {rows.bids.map((r, i) => (
          <div key={i} className="grid grid-cols-3 px-3 py-1 hover:bg-[#2B3139] font-mono">
            <div className="text-[#0ECB81]">{fmt(r.price)}</div>
            <div className="text-right text-[#EAECEF]">{r.qty.toFixed(4)}</div>
            <div className="text-right text-[#848E9C]">{fmt(r.total)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


function AlertButton({ symbol, currentPrice }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState('above');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await api.post('/alerts', { symbol, direction, target_price: parseFloat(target) });
      toast.success(`Alert set — we'll email you when ${symbol} ${direction === 'above' ? 'crosses' : 'drops below'} $${target}`);
      setOpen(false); setTarget('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-[#2B3139] bg-[#181A20] text-white hover:bg-[#2B3139] rounded-sm" data-testid="alert-btn">
          <Bell className="w-3.5 h-3.5 mr-1.5" /> Set Price Alert
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm">
        <DialogHeader><DialogTitle>Alert for {symbol}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-[#848E9C]">
            Current price: <span className="font-mono text-white">${currentPrice?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '—'}</span>
          </div>
          <div>
            <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Trigger When</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#181A20] border-[#2B3139] text-white">
                <SelectItem value="above">Crosses above ▲</SelectItem>
                <SelectItem value="below">Drops below ▼</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Target Price (USD)</Label>
            <Input type="number" value={target} onChange={e=>setTarget(e.target.value)} placeholder={currentPrice ? String(currentPrice.toFixed(2)) : '0.00'}
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono mt-1.5 focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
              data-testid="trade-alert-target" />
          </div>
          <Button onClick={create} disabled={busy || !target} className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold" data-testid="trade-confirm-alert">
            {busy ? 'Creating…' : 'Create Alert'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OrderForm({ symbol, price, onDone, wallet }) {
  const { user } = useAuth();
  const [side, setSide] = useState('buy');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const sym = symbol.toUpperCase();
  // /api/wallet returns balances as a LIST of {symbol, amount, ...} — normalize to map.
  const balMap = useMemo(
    () => Object.fromEntries((wallet?.balances || []).map(b => [String(b.symbol).toUpperCase(), Number(b.amount || 0)])),
    [wallet]
  );
  const usdtBal = balMap.USDT || 0;
  const coinBal = balMap[sym] || 0;
  const available = side === 'buy' ? usdtBal : coinBal;
  const availLabel = side === 'buy' ? 'USDT' : sym;

  const setPct = (p) => {
    if (!price || price <= 0) return;
    const raw = available * (p / 100);
    if (raw <= 0) { toast.info(`No available ${availLabel} to ${side}`); return; }
    // Slight buffer for buy to avoid float rounding above balance
    const safe = side === 'buy' ? Math.min(raw, usdtBal) : Math.min(raw, coinBal);
    setAmount(String(Number(safe.toFixed(6))));
  };

  const submit = async () => {
    if (!user) { toast.error('Sign in to trade'); return; }
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error('Enter a valid amount'); return; }
    if (side === 'buy' && val > usdtBal) { toast.error(`Insufficient USDT — you have $${usdtBal.toFixed(2)}`); return; }
    if (side === 'sell' && val > coinBal) { toast.error(`Insufficient ${sym} — you have ${coinBal.toFixed(6)}`); return; }
    setBusy(true);
    try {
      await api.post('/trade', {
        symbol: sym,
        side,
        amount_usd: val,
        price,
      });
      toast.success(`${side === 'buy' ? 'Bought' : 'Sold'} ${sym}`);
      setAmount('');
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Order failed');
    } finally { setBusy(false); }
  };

  const isBuy = side === 'buy';
  const est = parseFloat(amount) || 0;
  const estCoin = isBuy && price ? est / price : 0;
  const estUsd = !isBuy && price ? est * price : 0;
  return (
    <div className="p-4 space-y-4" data-testid="order-form">
      <Tabs value={side} onValueChange={(v) => { setSide(v); setAmount(''); }}>
        <TabsList className="grid grid-cols-2 bg-[#0B0E11] border border-[#2B3139] rounded-sm p-0 h-10">
          <TabsTrigger value="buy" data-testid="tab-buy"
            className="data-[state=active]:bg-[#0ECB81] data-[state=active]:text-white rounded-none h-full font-semibold">Buy</TabsTrigger>
          <TabsTrigger value="sell" data-testid="tab-sell"
            className="data-[state=active]:bg-[#F6465D] data-[state=active]:text-white rounded-none h-full font-semibold">Sell</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="border border-[#2B3139] bg-[#0B0E11] px-3 py-2 rounded-sm flex items-center justify-between" data-testid="order-avail-block">
        <span className="text-[10px] uppercase tracking-wider text-[#848E9C]">Available</span>
        <span className="font-mono text-white text-sm" data-testid="order-avail-value">
          {isBuy ? `$${usdtBal.toFixed(2)}` : `${coinBal.toFixed(6)}`}
          <span className="text-[#848E9C] text-xs ml-1">{availLabel}</span>
        </span>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#848E9C] mb-1.5">
          {isBuy ? 'Amount (USDT)' : `Amount (${sym})`}
        </div>
        <Input value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00" type="number"
          className="bg-[#0B0E11] border-[#2B3139] rounded-sm font-mono text-white h-10 focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
          data-testid="order-amount-input" />
      </div>
      <div className="flex gap-1.5">
        {[25, 50, 75, 100].map(p => (
          <button key={p} type="button" onClick={()=>setPct(p)}
            className="flex-1 text-xs text-[#848E9C] hover:text-white border border-[#2B3139] py-1 rounded-sm hover:border-[#FCD535]/60 hover:bg-[#FCD535]/5"
            data-testid={`order-pct-${p}`}>
            {p}%
          </button>
        ))}
      </div>
      {est > 0 && price > 0 && (
        <div className="text-[11px] text-[#848E9C] font-mono border-t border-[#2B3139] pt-2" data-testid="order-preview">
          {isBuy ? (
            <>You'll receive ≈ <span className="text-[#0ECB81]">{estCoin.toFixed(6)} {sym}</span></>
          ) : (
            <>You'll receive ≈ <span className="text-[#F6465D]">${estUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT</span></>
          )}
        </div>
      )}
      <div className="text-xs text-[#848E9C] font-mono">
        Price ≈ {price ? price.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'} USDT
      </div>
      <Button onClick={submit} disabled={busy || !user}
        className={`w-full h-11 rounded-sm font-semibold ${isBuy ? 'bg-[#0ECB81] hover:bg-[#0BB574] text-white' : 'bg-[#F6465D] hover:bg-[#E33A50] text-white'}`}
        data-testid={`order-submit-${side}`}>
        {!user ? 'Log in to trade' : busy ? 'Placing…' : `${isBuy ? 'Buy' : 'Sell'} ${sym}`}
      </Button>
    </div>
  );
}

export default function Trade() {
  const { coinId } = useParams();
  const { user } = useAuth();
  const [coin, setCoin] = useState(null);
  const [history, setHistory] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [myTrades, setMyTrades] = useState([]);
  const tickers = useBinanceTickers();

  const load = async () => {
    try {
      const [detail, hist] = await Promise.all([
        api.get(`/markets/${coinId}`),
        api.get(`/markets/${coinId}/history?days=7`),
      ]);
      setCoin(detail.data);
      setHistory((hist.data.prices || []).map(([t, p]) => ({ t: new Date(t).toLocaleDateString(), p })));
    } catch (_e) { /* noop */ }
  };
  const loadUserData = async () => {
    if (!user) { setWallet(null); setMyTrades([]); return; }
    try {
      const [w, t] = await Promise.all([
        api.get('/wallet'),
        api.get('/trades'),
      ]);
      setWallet(w.data);
      setMyTrades(t.data.trades || []);
    } catch (_e) { /* noop */ }
  };
  useEffect(() => { load(); }, [coinId]);
  useEffect(() => { loadUserData(); }, [user, coinId]);

  const { price, dir } = useLivePrice(coin?.current_price, tickers[coin?.symbol?.toUpperCase()]);

  if (!coin) return <div className="p-10 text-[#848E9C]">Loading market...</div>;

  const up = coin.price_change_percentage_24h >= 0;
  const sym = coin.symbol.toUpperCase();
  const symbolTrades = myTrades.filter(t => t.symbol === sym).slice(0, 20);

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-6" data-testid="trade-page">
      {/* Symbol header */}
      <div className="flex flex-wrap items-center gap-6 border-b border-[#2B3139] pb-4 mb-4">
        <div className="flex items-center gap-3">
          <img src={coin.image} alt="" className="w-9 h-9" />
          <div>
            <div className="text-white font-semibold text-lg">{coin.symbol.toUpperCase()}/USDT</div>
            <div className="text-[#848E9C] text-xs">{coin.name}</div>
          </div>
        </div>
        <div className={`font-mono text-2xl transition-colors ${dir === 'up' ? 'text-[#0ECB81]' : dir === 'down' ? 'text-[#F6465D]' : 'text-white'}`} data-testid="live-price">
          {price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">24h Change</div>
          <div className={`font-mono text-sm ${up ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
            {up ? '+' : ''}{coin.price_change_percentage_24h?.toFixed(2)}%
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">24h High</div>
          <div className="font-mono text-sm text-white">{coin.high_24h?.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">24h Low</div>
          <div className="font-mono text-sm text-white">{coin.low_24h?.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">24h Volume</div>
          <div className="font-mono text-sm text-white">${(coin.total_volume / 1e9).toFixed(2)}B</div>
        </div>
        <div className="ml-auto">
          <Link to="/markets" className="text-[#FCD535] text-sm hover:underline">← All markets</Link>
        </div>
      </div>

      {/* Quick actions */}
      <div className="mb-3 flex items-center gap-2">
        <AlertButton symbol={coin.symbol.toUpperCase()} currentPrice={price} />
        <ShareCoinButton coinId={coin.id} symbol={coin.symbol.toUpperCase()} />
      </div>

      {/* Grid: chart + order book + order form + news */}
      <div className="grid grid-cols-12 gap-[1px] bg-[#2B3139]">
        <div className="col-span-12 xl:col-span-6 bg-[#181A20] p-4 min-h-[420px]">
          <div className="text-xs uppercase tracking-wider text-[#848E9C] mb-3">Price · 7d</div>
          <div className="h-[380px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FCD535" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#FCD535" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="t" hide />
                <YAxis domain={['dataMin', 'dataMax']} tick={{ fill: '#848E9C', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`} />
                <Tooltip contentStyle={{ background: '#0B0E11', border: '1px solid #2B3139', borderRadius: 0, fontFamily: 'JetBrains Mono' }} labelStyle={{ color: '#848E9C' }} />
                <Area dataKey="p" stroke="#FCD535" strokeWidth={2} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="col-span-12 md:col-span-6 xl:col-span-2 bg-[#181A20]">
          <div className="px-3 py-2 border-b border-[#2B3139] text-[10px] uppercase tracking-wider text-[#EAECEF] font-semibold">Order Book</div>
          <OrderBook price={price} />
        </div>
        <div className="col-span-12 md:col-span-6 xl:col-span-2 bg-[#181A20]">
          <OrderForm symbol={coin.symbol} price={price || coin.current_price} onDone={() => { load(); loadUserData(); }} wallet={wallet} />
        </div>
        <div className="col-span-12 xl:col-span-2">
          <NewsRail compact />
        </div>
      </div>

      {/* My Trade History (Spot) — filtered to this coin */}
      {user && (
        <div className="mt-6 border border-[#2B3139] bg-[#181A20]" data-testid="spot-trade-history">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2B3139]">
            <div className="text-xs uppercase tracking-widest text-[#EAECEF] font-semibold">
              My {sym} Trades <span className="text-[#848E9C] normal-case tracking-normal ml-2">({symbolTrades.length})</span>
            </div>
            <Link to="/dashboard" className="text-[10px] uppercase font-mono text-[#FCD535] hover:underline" data-testid="view-all-trades-link">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]/60">
            <div className="col-span-2">Side</div>
            <div className="col-span-3 text-right">Amount ({sym})</div>
            <div className="col-span-2 text-right">Price</div>
            <div className="col-span-2 text-right">Total (USDT)</div>
            <div className="col-span-3 text-right">Date</div>
          </div>
          {symbolTrades.length === 0 ? (
            <div className="p-6 text-center text-[#848E9C] text-sm" data-testid="spot-trade-history-empty">
              No {sym} trades yet — place your first order above.
            </div>
          ) : (
            symbolTrades.map(t => (
              <div key={t.trade_id} className="grid grid-cols-12 px-4 py-2 border-b border-[#2B3139]/60 items-center text-xs" data-testid={`trade-row-${t.trade_id}`}>
                <div className={`col-span-2 font-mono uppercase font-semibold ${t.side === 'buy' ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>{t.side}</div>
                <div className="col-span-3 text-right font-mono text-[#EAECEF]">{Number(t.amount).toFixed(6)}</div>
                <div className="col-span-2 text-right font-mono text-[#B7BDC6]">${Number(t.price).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div className="col-span-2 text-right font-mono text-white">${Number(t.total_usd).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div className="col-span-3 text-right text-[#848E9C]">{new Date(t.created_at).toLocaleString()}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
